import { Fragment, ReactNode, useRef } from 'react';
import BgNsCta from '@britishgasenergy/react-nucleus/dist/nuc-components/NsCta';

import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { NsIconProps } from 'components/common/ns-icon/NsIcon';
import { beforeUnloadHandler } from 'hooks/useBeforeUnload';

type NsCtaProps = {
  type?: 'direct' | 'text' | 'submit' | 'reset';
  onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  isLoading?: boolean;
  href?: React.AnchorHTMLAttributes<HTMLAnchorElement>['href'];
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
  children?: ReactNode;
  loadingMessage?: string;
  icon?: NsIconProps['type'];
  testId?: string;
  slot?: string;
};

export const NsCta = ({
  onClick,
  href,
  isLoading,
  children,
  target,
  rel = 'noopener noreferrer',
  loadingMessage,
  icon,
  slot,
  type = 'direct',
  testId
}: NsCtaProps) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const Wrapper = href ? NsAnchor : Fragment; // workarround for ns-cta not allowing target attribute on links
  const wrapperProps = href ? { rel, href, target, testId } : {};
  const ctaProps = href ? {} : { 'data-testid': testId };
  const role = href ? 'link' : 'button';

  const externalLinkClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    onClick && onClick(event);

    window.removeEventListener('beforeunload', beforeUnloadHandler);
  };
  const onClickHandler =
    href && target === '_self' ? externalLinkClick : onClick;

  return (
    <Wrapper {...wrapperProps}>
      <BgNsCta
        {...ctaProps}
        role={role}
        type={type}
        loading={isLoading}
        loadingMessage={loadingMessage}
        icon={icon}
        slot={slot}
        onClick={onClickHandler}
        data-dd-action-name={labelRef?.current?.textContent}
      >
        {/* wrap text in span to avoid react crash when page is translated
        See: https://dev.azure.com.mcas.ms/planb-platforms/customer.centric.layer/_workitems/edit/35845
        */}
        <span ref={labelRef}>{children}</span>
      </BgNsCta>
    </Wrapper>
  );
};
